import * as Select from '@radix-ui/react-select';
import { Trans, useTranslation } from 'react-i18next';

import { Scrollbars } from '~/chords/Scrollbars';
import { DropdownItems } from '~/components/DropdownTreeSingleSelect/components/DropdownItems';
import { DropdownNoOptionsAvailable } from '~/components/DropdownTreeSingleSelect/components/DropdownNoOptionsAvailable';
import { DropdownSearch } from '~/components/DropdownTreeSingleSelect/components/DropdownSearch';
import type {
  DropdownSearchOptions,
  InternalDropdownOption,
  OptionTemplateComponent,
  RadixUiProps,
} from '~/components/DropdownTreeSingleSelect/types';
import { cn } from '~/lib/cn';

interface DropdownMenuProps extends RadixUiProps {
  options: InternalDropdownOption[];
  searchOptions?: DropdownSearchOptions;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  selectedOption?: InternalDropdownOption;
  onSelectOption: (option: InternalDropdownOption) => void;
  onExpandOption: (option: InternalDropdownOption, isExpanded: boolean) => void;
  showCheckIcon?: boolean;
  OptionTemplate?: OptionTemplateComponent;
}

export function DropdownMenu({
  options,
  searchOptions,
  searchQuery,
  onSearchQueryChange,
  selectedOption,
  onSelectOption,
  onExpandOption,
  showCheckIcon,
  OptionTemplate,
  radix = {},
}: DropdownMenuProps) {
  const { t } = useTranslation('common');
  const isOneOrMoreOptionsVisible = options.some((option) => option.isVisible);
  const searchQueryTrimmed = searchQuery.trim();

  return (
    <Select.Portal>
      <Select.Content
        // Prevents the dropdown menu from overlapping the dropdown trigger.
        // https://www.radix-ui.com/primitives/docs/components/select#content
        position="popper"
        {...radix.selectContent}
        className={cn(
          'mt-xs min-w-[var(--radix-popper-anchor-width)] overflow-auto text-body-sm text-default',
          'rounded-xs border shadow-xl drop-shadow-xl bg-level-03 border-emphasis-strong',
          radix.selectContent?.className,
        )}
        style={
          {
            // This is used to calculate the scroll height of the dropdown menu.
            '--dropdown-menu-search-height': searchOptions?.isEnabled
              ? '72px'
              : '0px',
          } as React.CSSProperties
        }
      >
        {!searchQuery && !options?.length ? (
          <DropdownNoOptionsAvailable radix={radix} />
        ) : (
          <>
            {searchOptions?.isEnabled ? (
              <DropdownSearch
                value={searchQuery}
                onChange={onSearchQueryChange}
              />
            ) : null}
            <Select.Viewport {...radix.selectViewport}>
              <Scrollbars asChild>
                <div className="max-h-dropdown-tree-single-select-scroll">
                  {isOneOrMoreOptionsVisible ? (
                    <DropdownItems
                      depth={0}
                      options={options}
                      searchQuery={searchQueryTrimmed}
                      selectedOption={selectedOption}
                      onSelectOption={onSelectOption}
                      onExpandOption={onExpandOption}
                      showCheckIcon={showCheckIcon}
                      OptionTemplate={OptionTemplate}
                      radix={radix}
                    />
                  ) : (
                    <DropdownNoOptionsAvailable
                      labelText={t('dropdownTree.noMatchingOptionsText', {
                        searchQuery: searchQueryTrimmed,
                      })}
                      labelNode={
                        <span className="text-emphasis-stronger">
                          <Trans
                            i18nKey="dropdownTree.noMatchingOptionsHtml"
                            t={t}
                            values={{
                              searchQuery: searchQueryTrimmed,
                            }}
                            components={{
                              strong: (
                                <span className="font-semibold text-default" />
                              ),
                            }}
                          />
                        </span>
                      }
                      radix={radix}
                    />
                  )}
                </div>
              </Scrollbars>
            </Select.Viewport>
          </>
        )}
      </Select.Content>
    </Select.Portal>
  );
}

DropdownMenu.displayName = 'DropdownMenu';
